// Copyright (c) 2021 embyt GmbH. All rights reserved.
// Author: Roman Morawek

import * as React from "react";
import { connect } from "react-redux";
import { FormattedMessage, defineMessages, WrappedComponentProps, injectIntl } from "react-intl";
import { Button, Form, Collapse } from "react-bootstrap";

import { UserRole } from "graphql/api";
import { IAction, ActionType, raiseAction, IActionTypeEdit } from "actions";
import { DeviceType, Report, ReportsSettings } from "dbtypes";
import {
  getLocalDateTimeIso,
  getLocalTime,
  getLocalDate,
  isSet,
  getBrowserTimezoneOffset,
} from "utils";
import { isOcppFrontend, parseDateTimeEvent } from "guiutils";
import { IState } from "states/states";
import { IReportOptions, IReportState } from "states/guistates";
import { DataState } from "types";

import ModalReportSettings from "view/modal/ModalReportSettings";
import ReportEntry from "view/comp/ReportEntry";

import { ReactComponent as IconExpandMore } from "static/iconExpandMore.svg";
import { ReactComponent as IconExpandLess } from "static/iconExpandLess.svg";

interface IReportsExtProps {
  isDashboard?: boolean;
  isReadOnly: boolean;
  isAdminPage?: boolean;
}

type IReportsState = ReportsSettings & {
  isExpanded: boolean;
};

type IReportsProps = IReportOptions & {
  isAdminUser: boolean;
  isOcppRestricted: boolean;
  isVirtualSite: boolean;
  canEditReports: boolean;
  siteId: number | undefined;
  reports: Report[];
  reportLinks: (IReportState | null)[];
  dataState: DataState;
};

const mapStateToProps = (state: IState, ownProps: IReportsExtProps) => {
  const siteId = state.cached.selectedSite;
  const site = state.site.siteList?.find((curSite) => curSite.id === siteId);
  if (site === undefined) {
    console.error("admin unidentified site", siteId, state.site.siteList);
  }
  const curUser = state.site.userList?.find((curItem) => curItem.secId === state.site.user?.secId);
  const props: IReportsProps = {
    ...state.gui.reportOptions,
    isAdminUser: [UserRole.admin, UserRole.root].includes(curUser?.role ?? UserRole.viewer),
    isOcppRestricted: isOcppFrontend(state.router.rootPath),
    isVirtualSite: site?.deviceType === DeviceType.VIRTUAL,
    canEditReports: curUser?.accessRights.createReports ?? false,
    siteId,
    reports: site?.cloudSettings?.reports ?? [],
    reportLinks: state.gui.reportLinks,
    dataState: state.scheduler.saveState,
  };
  return props;
};

interface IReportsDispatch {
  onSetValue: (name: keyof IReportOptions, value: number | boolean | undefined) => void;
  onNewReport: () => void;
  setDirtyFlag: () => void;
}

const mapDispatchToProps = (dispatch: (action: IAction<any>) => void) => {
  const dispatchProps: IReportsDispatch = {
    onSetValue: (name: keyof IReportOptions, value: number | boolean | undefined) => {
      const editPayload: IActionTypeEdit = {
        name: `reportOptions.${name}`,
        value,
      };
      dispatch(raiseAction(ActionType.ACT_EDIT_VALUE, editPayload));
    },
    onNewReport: () => {
      dispatch(raiseAction(ActionType.ACT_EDIT_REPORT, undefined));
    },
    setDirtyFlag: () => {
      dispatch(raiseAction(ActionType.ACT_SET_PAGE_DIRTY, true));
    },
  };
  return dispatchProps;
};

const messages = defineMessages({
  startFilterHint: {
    id: "start date",
  },
  endFilterHint: {
    id: "end date",
  },
});

class ReportsComponent extends React.Component<
  IReportsProps & IReportsExtProps & IReportsDispatch & WrappedComponentProps,
  IReportsState
> {
  state: IReportsState = {
    isExpanded: this.props.isAdminPage !== true,
  };

  public render() {
    const usedReports = Array.from(Array(this.props.reports.length).keys()).filter(
      (curItem) =>
        (!this.props.isReadOnly || this.props.reports[curItem].isActive) &&
        (this.props.isAdminUser || this.props.reports[curItem].type !== "site") &&
        (!this.props.isOcppRestricted || this.props.reports[curItem].type === "charger"),
    );
    const reportItems = usedReports.map((curItem) => {
      return <ReportEntry key={curItem} id={curItem} isReadOnly={this.props.isReadOnly} />;
    });

    return (
      <div id="reports">
        {this.props.isDashboard !== true && (
          <h1
            className="link"
            onClick={() => {
              // only allow expand/collapse on admin-page!
              if (this.props.isAdminPage === true) {
                this.setState({ isExpanded: !this.state.isExpanded });
              }
            }}
          >
            {this.props.isAdminPage === true && (
              // only show expand/collapse button on admin-page
              <Button variant="link" className="expand-button me-2 d-print-none">
                {this.state.isExpanded ? <IconExpandMore /> : <IconExpandLess />}
              </Button>
            )}
            <FormattedMessage id="Reports" />
          </h1>
        )}
        <Collapse in={this.state.isExpanded}>
          <div>
            {this.renderTimeFilter()}
            <div id="reportentries">
              <div className="tableheader">
                <FormattedMessage id="Report Items" />
              </div>
              <table>
                <thead>
                  <tr>
                    <th></th>
                    <th></th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {reportItems}
                  {!this.props.isReadOnly && (
                    <tr>
                      <td colSpan={2}>
                        <Button
                          id="newreport"
                          variant="secondary"
                          className="mt-1 mb-1"
                          onClick={(e) => this.props.onNewReport()}
                        >
                          <FormattedMessage id="Create New" />
                        </Button>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </Collapse>
        <ModalReportSettings />
      </div>
    );
  }

  private renderTimeFilter() {
    // today, 00:00
    const today =
      (Math.round(Date.now() / (24 * 60 * 60 * 1000)) * 24 * 60 * 60 + getBrowserTimezoneOffset()) *
      1000;
    return (
      <div id="reportoptions">
        <div className="tableheader mb-1">
          <FormattedMessage id="Report Options" />
        </div>
        <table>
          <tbody>
            <tr>
              <td>
                <Form.Check
                  id="limitStart"
                  checked={this.props.startDate ? true : false}
                  onChange={(event) => {
                    this.props.onSetValue(
                      "startDate",
                      event.currentTarget.checked ? today : undefined,
                    );
                  }}
                  label={
                    <>
                      <FormattedMessage id="Limit period from" />
                      {isSet(this.props.startDate) && this.props.startDate !== 0 && <>:</>}
                    </>
                  }
                />
              </td>
              <td>
                {isSet(this.props.startDate) && this.props.startDate !== 0 && (
                  <>
                    {Modernizr.inputtypes["datetime-local"] ? (
                      <input
                        id="datetime_limitStart"
                        type="datetime-local"
                        name="timestamp"
                        style={{ width: 210 }}
                        className="me-1"
                        title={this.props.intl.formatMessage(messages.startFilterHint)}
                        defaultValue={getLocalDateTimeIso(this.props.startDate)}
                        onChange={(event) => {
                          const newDate = parseDateTimeEvent(event, this.props.startDate);
                          // gets undefined using "clear", but also by entering a date with zero
                          if (newDate) {
                            this.props.onSetValue("startDate", newDate);
                          }
                        }}
                      />
                    ) : (
                      <>
                        <input
                          id="date_limitStart"
                          type="date"
                          name="date"
                          style={{ width: 130 }}
                          title={this.props.intl.formatMessage(messages.startFilterHint)}
                          defaultValue={getLocalDate(this.props.startDate)}
                          onChange={(event) => {
                            this.props.onSetValue(
                              "startDate",
                              parseDateTimeEvent(event, this.props.startDate),
                            );
                          }}
                        />
                        <input
                          id="time_limitStart"
                          type="time"
                          name="time"
                          style={{ width: 145 }}
                          title={this.props.intl.formatMessage(messages.startFilterHint)}
                          defaultValue={getLocalTime(this.props.startDate)}
                          onChange={(event) => {
                            this.props.onSetValue(
                              "startDate",
                              parseDateTimeEvent(event, this.props.startDate),
                            );
                          }}
                        />
                        {" - "}
                      </>
                    )}
                  </>
                )}
              </td>
            </tr>
            <tr>
              <td>
                <Form.Check
                  id="limitEnd"
                  checked={this.props.endDate ? true : false}
                  onChange={(event) => {
                    this.props.onSetValue(
                      "endDate",
                      event.currentTarget.checked ? today : undefined,
                    );
                  }}
                  label={
                    <>
                      <FormattedMessage id="Limit period until" />
                      {isSet(this.props.endDate) && this.props.endDate !== 0 && <>:</>}
                    </>
                  }
                />
              </td>
              <td>
                {isSet(this.props.endDate) && this.props.endDate !== 0 && (
                  <>
                    {Modernizr.inputtypes["datetime-local"] ? (
                      <input
                        id="datetime_limitEnd"
                        type="datetime-local"
                        name="timestamp"
                        style={{ width: 210 }}
                        title={this.props.intl.formatMessage(messages.endFilterHint)}
                        defaultValue={getLocalDateTimeIso(this.props.endDate)}
                        onChange={(event) => {
                          const newDate = parseDateTimeEvent(event, this.props.endDate);
                          // gets undefined using "clear", but also by entering a date with zero
                          if (newDate) {
                            this.props.onSetValue("endDate", newDate);
                          }
                        }}
                      />
                    ) : (
                      <>
                        <input
                          id="date_limitEnd"
                          type="date"
                          name="date"
                          style={{ width: 130 }}
                          title={this.props.intl.formatMessage(messages.endFilterHint)}
                          defaultValue={getLocalDate(this.props.endDate)}
                          onChange={(event) => {
                            this.props.onSetValue(
                              "endDate",
                              parseDateTimeEvent(event, this.props.endDate),
                            );
                          }}
                        />
                        <input
                          id="time_limitEnd"
                          type="time"
                          name="time"
                          style={{ width: 145 }}
                          title={this.props.intl.formatMessage(messages.endFilterHint)}
                          defaultValue={getLocalTime(this.props.endDate)}
                          onChange={(event) => {
                            this.props.onSetValue(
                              "endDate",
                              parseDateTimeEvent(event, this.props.endDate),
                            );
                          }}
                        />
                      </>
                    )}
                  </>
                )}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    );
  }
}

export default injectIntl(connect(mapStateToProps, mapDispatchToProps)(ReportsComponent));
